import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { API, formatApiErrorDetail } from "../App";
import { useTranslation } from "react-i18next";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { MapPin, CheckCircle } from "lucide-react";
import axios from "axios";
import { useBranding } from "../contexts/BrandingContext";

export default function ResetPasswordPage() {
  const { t, i18n } = useTranslation();
  const { branding } = useBranding();
  const brandName = (i18n.language === "el" && branding?.brand_name_el) || branding?.brand_name || "TableCy";
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const navigate = useNavigate();

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token) {
      navigate("/forgot-password", { replace: true });
    }
  }, [token, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    if (password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: password });
      setSuccess(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-8">
      <div className="w-full max-w-md space-y-8">
        {/* Logo (branding-driven) */}
        <Link to="/" className="flex items-center gap-2 text-primary">
          {branding?.use_image && branding?.logo_url ? (
            <img src={branding.logo_url} alt={brandName} className="h-8 w-8 object-contain" />
          ) : branding?.logo_svg ? (
            <span className="h-8 w-8 inline-flex items-center justify-center" dangerouslySetInnerHTML={{ __html: branding.logo_svg }} />
          ) : (
            <MapPin className="h-8 w-8" />
          )}
          <span className="text-2xl font-semibold">{brandName}</span>
        </Link>

        {success ? (
          <div className="space-y-6">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <div className="text-center">
              <h1 className="text-2xl font-medium text-foreground">Password reset!</h1>
              <p className="mt-2 text-muted-foreground">
                Your password has been successfully reset. You can now log in with your new password.
              </p>
            </div>
            <Button asChild className="w-full">
              <Link to="/login">Continue to login</Link>
            </Button>
          </div>
        ) : (
          <>
            <div>
              <h1 className="text-3xl font-medium text-foreground">Set new password</h1>
              <p className="mt-2 text-muted-foreground">
                Your new password must be at least 6 characters.
              </p>
            </div>

            {error && (
              <div className="bg-destructive/10 text-destructive px-4 py-3 rounded-lg text-sm" data-testid="reset-password-error">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="password">New Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  data-testid="reset-password-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm Password</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  data-testid="reset-password-confirm-input"
                />
              </div>

              <Button type="submit" className="w-full" disabled={loading} data-testid="reset-password-submit-button">
                {loading ? "Resetting..." : "Reset password"}
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
